// Questionnaire v2 — the typed-question renderer (2026-10-21).
//
// Used by the PUBLIC fill page (pages/QuestionnaireFill.js) and by the
// template builder's preview (components/QuestionnaireQuestionsEditor.js), so
// an admin previews exactly what a client gets.  It never talks to the network
// and never imports services/api.js: the public page has no staff session.
//
// Contract (docs/specs/questionnaire-v2-civil-rights.md): the snapshot is a
// TREE (node.children).  A follow-up with show_when is shown only while its
// parent's answer equals it; show_when null = always shown under its parent.
// Hidden answers stay in the caller's memory — the caller sends only the
// visible ones (lib/questionnaireTree.js visibleAnswers).
//
// Built for a phone: one column, 16 px inputs (iOS zooms on anything
// smaller), 44 px tap targets, native date pickers, inputmode/autocomplete on
// the contact fields.  Accessibility: radio groups are fieldset + legend;
// follow-ups render directly after their parent (aria-controls on the radios)
// and a polite live region says when they appear — focus never jumps.
//
// Every component is defined at MODULE scope.  A component declared inside
// another's render is a new type each render, which remounts its subtree and
// drops the focus out of the box being typed in.

import {
  indexTree, makeVisibility, wordCount, blankRow, rowsForDisplay, ruleMessage,
  localToday, addYears, YES_NO_LABELS,
} from '../lib/questionnaireTree'

const INPUT = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-600'
const INPUT_ERR = 'border-red-500 bg-red-50'

function Title ({ node, htmlFor, id, as = 'label' }) {
  const Tag = as
  const props = as === 'label' ? { htmlFor } : {}
  return (
    <Tag id={id} {...props} className="block text-base font-medium text-gray-900 mb-1.5 break-words">
      {node.number_label && <span className="font-semibold text-gray-700 mr-1">{node.number_label}.</span>}
      <span>{node.label}</span>
      {node.is_required && <span className="text-red-600 font-semibold ml-1" aria-hidden="true">*</span>}
      {node.is_required && <span className="sr-only"> (required)</span>}
    </Tag>
  )
}

function Help ({ node, id }) {
  if (!node.help_text) return null
  return <p id={id} className="text-sm text-gray-500 mb-2 break-words">{node.help_text}</p>
}

function FieldError ({ id, message }) {
  if (!message) return null
  return <p id={id} className="text-sm text-red-700 mt-1.5">{message}</p>
}

function describedBy (...ids) {
  const s = ids.filter(Boolean).join(' ')
  return s || undefined
}

// ── short answer ──
function ShortQ ({ node, value, onChange, disabled, error }) {
  const id = `qnr-in-${node.id}`
  const numeric = !!(node.options && node.options.numeric)
  const max = numeric ? 3 : (node.max_chars || 500)
  const helpId = node.help_text ? `qnr-help-${node.id}` : null
  const errId = error ? `qnr-err-${node.id}` : null
  return (
    <div>
      <Title node={node} htmlFor={id} />
      <Help node={node} id={helpId} />
      <input id={id} type="text" value={value || ''} disabled={disabled}
        onChange={e => onChange(numeric ? e.target.value.replace(/\D/g, '').slice(0, 3) : e.target.value.slice(0, max))}
        maxLength={max} inputMode={numeric ? 'numeric' : undefined} pattern={numeric ? '[0-9]*' : undefined}
        aria-invalid={error ? true : undefined} aria-describedby={describedBy(helpId, errId)}
        className={`${INPUT} ${numeric ? 'max-w-[8rem]' : ''} ${error ? INPUT_ERR : ''}`} />
      <FieldError id={errId} message={error && ruleMessage(error, node)} />
    </div>
  )
}

// ── date ──
function DateQ ({ node, value, onChange, disabled, error, today }) {
  const id = `qnr-in-${node.id}`
  const helpId = node.help_text ? `qnr-help-${node.id}` : null
  const errId = error ? `qnr-err-${node.id}` : null
  const allowFuture = !!(node.options && node.options.allow_future)
  return (
    <div>
      <Title node={node} htmlFor={id} />
      <Help node={node} id={helpId} />
      <input id={id} type="date" value={value || ''} disabled={disabled}
        onChange={e => onChange(e.target.value)}
        min="1900-01-01" max={allowFuture ? addYears(today, 10) : today}
        aria-invalid={error ? true : undefined} aria-describedby={describedBy(helpId, errId)}
        className={`${INPUT} max-w-[14rem] ${error ? INPUT_ERR : ''}`} />
      <FieldError id={errId} message={error && ruleMessage(error, node)} />
    </div>
  )
}

// ── yes/no and multiple choice: one radio group ──
function ChoiceQ ({ node, value, onChange, disabled, error, controls }) {
  const legendId = `qnr-lg-${node.id}`
  const helpId = node.help_text ? `qnr-help-${node.id}` : null
  const errId = error ? `qnr-err-${node.id}` : null
  const opts = node.type === 'yes_no'
    ? ['yes', 'no', ...(node.options && node.options.allow_unknown ? ['unknown'] : [])].map(k => ({ key: k, label: YES_NO_LABELS[k] }))
    : ((node.options && node.options.choices) || [])
  const inline = node.type === 'yes_no'
  return (
    <fieldset role="radiogroup" aria-labelledby={legendId} aria-describedby={describedBy(helpId, errId)}
      aria-invalid={error ? true : undefined}>
      <Title node={node} id={legendId} as="legend" />
      <Help node={node} id={helpId} />
      <div className={inline ? 'flex flex-wrap gap-2' : 'space-y-2'}>
        {opts.map(o => {
          const checked = value === o.key
          return (
            <label key={o.key}
              className={`flex items-center gap-3 min-h-[44px] px-4 py-2 rounded-lg border cursor-pointer text-base select-none ${inline ? '' : 'w-full'} ${
                checked ? 'border-primary-600 bg-primary-50 text-primary-900 font-medium' : error ? 'border-red-400 bg-white' : 'border-gray-300 bg-white hover:bg-gray-50'} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
              <input type="radio" name={`qnr-radio-${node.id}`} value={o.key} checked={checked} disabled={disabled}
                onChange={() => onChange(o.key)} aria-controls={controls || undefined}
                className="h-5 w-5 flex-shrink-0" />
              <span className="break-words">{o.label}</span>
            </label>
          )
        })}
      </div>
      {value && !disabled && (
        <button type="button" onClick={() => onChange('')}
          className="mt-1.5 text-sm text-gray-500 hover:text-gray-700 underline min-h-[44px] px-1">
          Clear answer
        </button>
      )}
      <FieldError id={errId} message={error && ruleMessage(error, node)} />
    </fieldset>
  )
}

// ── long answer + word guide ──
// The word guide is a SUGGESTION (Shawn's "500-600 word description"); only
// max_chars is enforced.  The counter's live region speaks only when a
// threshold is crossed, never on every keystroke.
function wordStage (n, min, max) {
  if (max != null && n > max) return 'over'
  if (min != null && n >= min) return 'in'
  return n > 0 ? 'some' : 'none'
}
function LongQ ({ node, value, onChange, disabled, error }) {
  const id = `qnr-in-${node.id}`
  const helpId = node.help_text ? `qnr-help-${node.id}` : null
  const errId = error ? `qnr-err-${node.id}` : null
  const countId = `qnr-count-${node.id}`
  const max = node.max_chars || 6000
  const text = value || ''
  const words = wordCount(text)
  const gMin = node.word_guide_min ?? null
  const gMax = node.word_guide_max ?? null
  const hasGuide = gMin != null || gMax != null
  const guide = gMin != null && gMax != null ? `${gMin}–${gMax} words` : gMax != null ? `up to ${gMax} words` : gMin != null ? `at least ${gMin} words` : ''
  const stage = wordStage(words, gMin, gMax)
  const nearCap = text.length >= max * 0.9
  const rows = gMax != null && gMax >= 400 ? 10 : gMax != null && gMax >= 100 ? 6 : 4
  const spoken = text.length >= max ? 'Character limit reached.'
    : nearCap ? 'Approaching the character limit.'
      : stage === 'over' ? `Over the suggested ${guide}.`
        : stage === 'in' ? `Within the suggested ${guide}.` : ''
  return (
    <div>
      <Title node={node} htmlFor={id} />
      <Help node={node} id={helpId} />
      {hasGuide && <p className="text-sm text-gray-500 mb-2">Suggested length: {guide}.</p>}
      <textarea id={id} value={text} disabled={disabled} rows={rows}
        onChange={e => onChange(e.target.value.slice(0, max))} maxLength={max}
        aria-invalid={error ? true : undefined} aria-describedby={describedBy(helpId, countId, errId)}
        className={`${INPUT} leading-relaxed ${error ? INPUT_ERR : ''}`} />
      <div id={countId} className={`flex flex-wrap justify-between gap-x-3 text-sm mt-1 ${stage === 'over' ? 'text-amber-700' : stage === 'in' ? 'text-emerald-700' : 'text-gray-500'}`}>
        <span>{words.toLocaleString()} {words === 1 ? 'word' : 'words'}{hasGuide ? ` · suggested ${guide}` : ''}{stage === 'over' ? ' · a little long, which is fine' : ''}</span>
        {nearCap && <span className={text.length >= max ? 'text-red-700' : 'text-amber-700'}>{text.length.toLocaleString()} / {max.toLocaleString()} characters</span>}
      </div>
      <span className="sr-only" aria-live="polite">{spoken}</span>
      <FieldError id={errId} message={error && ruleMessage(error, node)} />
    </div>
  )
}

// ── repeating rows ──
function RepeatQ ({ node, value, onChange, disabled, error, rowErrors, today }) {
  const legendId = `qnr-lg-${node.id}`
  const helpId = node.help_text ? `qnr-help-${node.id}` : null
  const errId = error ? `qnr-err-${node.id}` : null
  const fields = node.row_fields || []
  const max = node.max_rows || 10
  const rows = rowsForDisplay(node, value)
  const ref = node.number_label || 'this question'
  const setCell = (i, key, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)))
  const add = () => { if (rows.length < max) onChange([...rows, blankRow(node)]) }
  const remove = (i) => {
    const next = rows.filter((_, j) => j !== i)
    onChange(next.length ? next : [blankRow(node)])
  }
  return (
    <fieldset aria-labelledby={legendId} aria-describedby={describedBy(helpId, errId)}>
      <Title node={node} id={legendId} as="legend" />
      <Help node={node} id={helpId} />
      <ol className="space-y-3">
        {rows.map((r, i) => {
          const re = (rowErrors && rowErrors[i]) || {}
          return (
            <li key={r._id} className="border border-gray-200 rounded-xl p-3 bg-gray-50">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-sm font-semibold text-gray-600">Entry {i + 1}</span>
                {(rows.length > 1 || fields.some(f => r[f.key])) && (
                  <button type="button" onClick={() => remove(i)} disabled={disabled}
                    aria-label={`Remove entry ${i + 1} from ${ref}`}
                    className="text-sm text-red-700 hover:text-red-900 min-h-[44px] px-2 disabled:opacity-50">
                    Remove
                  </button>
                )}
              </div>
              <div className={`grid gap-3 ${fields.length > 1 ? 'sm:grid-cols-2' : ''}`}>
                {fields.map(f => {
                  const cid = `qnr-in-${node.id}-${r._id}-${f.key}`
                  const cellErr = re[f.key]
                  const cellErrId = cellErr ? `${cid}-err` : null
                  const long = f.type === 'short' && (f.max_chars || 300) > 300
                  return (
                    <div key={f.key} className={long && fields.length > 1 ? 'sm:col-span-2' : ''}>
                      <label htmlFor={cid} className="block text-sm font-medium text-gray-700 mb-1">
                        {f.label}<span className="sr-only"> (entry {i + 1})</span>
                      </label>
                      {f.type === 'date'
                        ? <input id={cid} type="date" value={r[f.key] || ''} disabled={disabled} min="1900-01-01" max={today}
                            onChange={e => setCell(i, f.key, e.target.value)}
                            aria-invalid={cellErr ? true : undefined} aria-describedby={cellErrId || undefined}
                            className={`${INPUT} max-w-[14rem] ${cellErr ? INPUT_ERR : ''}`} />
                        : long
                          ? <textarea id={cid} value={r[f.key] || ''} disabled={disabled} rows={3} maxLength={f.max_chars || 300}
                              onChange={e => setCell(i, f.key, e.target.value)}
                              aria-invalid={cellErr ? true : undefined} aria-describedby={cellErrId || undefined}
                              className={`${INPUT} ${cellErr ? INPUT_ERR : ''}`} />
                          : <input id={cid} type="text" value={r[f.key] || ''} disabled={disabled} maxLength={f.max_chars || 300}
                              onChange={e => setCell(i, f.key, e.target.value)}
                              aria-invalid={cellErr ? true : undefined} aria-describedby={cellErrId || undefined}
                              className={`${INPUT} ${cellErr ? INPUT_ERR : ''}`} />}
                      <FieldError id={cellErrId} message={cellErr && ruleMessage(cellErr, node)} />
                    </div>
                  )
                })}
              </div>
            </li>
          )
        })}
      </ol>
      {rows.length < max ? (
        <button type="button" onClick={add} disabled={disabled} aria-label={`Add an entry to ${ref}`}
          className="mt-3 w-full sm:w-auto min-h-[44px] px-4 py-2 rounded-lg border border-dashed border-primary-400 text-primary-700 font-medium hover:bg-primary-50 disabled:opacity-50">
          + Add another entry
        </button>
      ) : (
        <p className="mt-2 text-sm text-gray-500">That is the most entries this question can hold ({max}).</p>
      )}
      <FieldError id={errId} message={error && ruleMessage(error, node)} />
    </fieldset>
  )
}

// ── contact field (decision 7: "Partly hidden + Correct?") ──
// A detail the firm has on file is shown partly hidden with "Correct?"
// Yes → {confirmed:true}; Change → a box for the corrected value.  The real
// value never reaches this page, so "Change" always starts blank.
const CONTACT_INPUT = {
  full_name: { autoComplete: 'name' },
  street_address: { autoComplete: 'street-address' },
  city_state_zip: { autoComplete: 'off' },
  phone: { autoComplete: 'tel', inputMode: 'tel', type: 'tel' },
  cell_phone: { autoComplete: 'tel', inputMode: 'tel', type: 'tel' },
  email: { autoComplete: 'email', inputMode: 'email', type: 'email' },
}
function ContactQ ({ node, field, answer, onChange, disabled, error }) {
  const key = node.contact_key
  const id = `qnr-in-${node.id}`
  const labelId = `qnr-lbl-${node.id}`
  const errId = error ? `qnr-err-${node.id}` : null
  const masked = field && field.prefill_masked
  const attrs = CONTACT_INPUT[key] || {}
  const max = node.max_chars || 300
  const mode = answer && answer.confirmed === true ? 'confirmed' : answer && typeof answer.value === 'string' ? 'change' : 'ask'
  const input = (
    <input id={id} type={attrs.type || 'text'} value={(answer && answer.value) || ''} disabled={disabled}
      onChange={e => onChange({ value: e.target.value.slice(0, max) })} maxLength={max}
      autoComplete={attrs.autoComplete} inputMode={attrs.inputMode}
      aria-labelledby={masked ? labelId : undefined}
      aria-invalid={error ? true : undefined} aria-describedby={errId || undefined}
      className={`${INPUT} ${error ? INPUT_ERR : ''}`} />
  )
  if (!masked) {
    return (
      <div>
        <Title node={node} htmlFor={id} />
        {input}
        <FieldError id={errId} message={error && ruleMessage(error, node)} />
      </div>
    )
  }
  return (
    <div role="group" aria-labelledby={labelId}>
      <Title node={node} id={labelId} as="div" />
      {mode === 'change' ? (
        <div className="space-y-1">
          {input}
          <button type="button" onClick={() => onChange(null)} disabled={disabled}
            className="text-sm text-primary-700 hover:text-primary-900 underline min-h-[44px] px-1 disabled:opacity-50">
            Use the details on file ({masked})
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-sm text-gray-500">On file:</span>
          <span className="font-mono text-base text-gray-900 break-all">{masked}</span>
          {mode === 'confirmed' ? (
            <>
              <span className="text-emerald-700 font-medium">✓ Confirmed</span>
              <button type="button" onClick={() => onChange({ value: '' })} disabled={disabled}
                className="min-h-[44px] px-4 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-base disabled:opacity-50">
                Change
              </button>
            </>
          ) : (
            <span className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Correct?</span>
              <button type="button" onClick={() => onChange({ confirmed: true })} disabled={disabled}
                className="min-h-[44px] px-4 rounded-lg border border-primary-600 text-primary-700 bg-white hover:bg-primary-50 font-medium text-base disabled:opacity-50">
                Yes
              </button>
              <button type="button" onClick={() => onChange({ value: '' })} disabled={disabled}
                className="min-h-[44px] px-4 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-base disabled:opacity-50">
                Change
              </button>
            </span>
          )}
        </div>
      )}
      <FieldError id={errId} message={error && ruleMessage(error, node)} />
    </div>
  )
}

function UploadPlaceholder ({ node }) {
  return (
    <div role="group" aria-labelledby={`qnr-lg-${node.id}`} className="border-2 border-dashed border-gray-300 rounded-xl p-4 bg-gray-50">
      <Title node={node} id={`qnr-lg-${node.id}`} as="div" />
      <Help node={node} />
      <p className="text-sm text-gray-500">The client picks PDFs or photos of documents here.  Files are virus-scanned and kept with the lead's records.</p>
    </div>
  )
}

// One node + its visible follow-ups.
function QuestionNode ({ node, depth, ctx }) {
  const k = String(node.id)
  const err = ctx.errors[k] || null
  const kids = (node.children || []).filter(c => ctx.vis(c))
  const hasKids = (node.children || []).length > 0
  const fuId = hasKids ? `qnr-fu-${node.id}` : null
  const set = (v) => ctx.onAnswer(node, v)
  const common = { node, disabled: ctx.disabled, error: err && err.rule, today: ctx.today }
  let control
  switch (node.type) {
    case 'short': control = <ShortQ {...common} value={ctx.answers[k]} onChange={set} />; break
    case 'date': control = <DateQ {...common} value={ctx.answers[k]} onChange={set} />; break
    case 'yes_no': case 'choice': control = <ChoiceQ {...common} value={ctx.answers[k]} onChange={set} controls={fuId} />; break
    case 'long': control = <LongQ {...common} value={ctx.answers[k]} onChange={set} />; break
    case 'repeat': control = <RepeatQ {...common} value={ctx.answers[k]} onChange={set} rowErrors={err && err.rows} />; break
    case 'contact_field': {
      const field = (ctx.contactFields || []).find(f => String(f.question_id) === k || f.key === node.contact_key) || null
      control = <ContactQ {...common} field={field} answer={ctx.contact[node.contact_key]} onChange={v => ctx.onContact(node.contact_key, v)} />
      break
    }
    case 'upload': control = ctx.renderUpload ? ctx.renderUpload(node) : <UploadPlaceholder node={node} />; break
    default: control = <p className="text-sm text-gray-500">{node.label}</p>
  }
  return (
    <div id={`qnr-q-${node.id}`} className={`scroll-mt-24 ${depth > 1 ? 'pl-3 sm:pl-4 border-l-4 border-primary-100' : ''}`}>
      {control}
      {hasKids && (
        <div id={fuId} className={kids.length ? 'mt-5 space-y-6' : ''}>
          {kids.map(c => <QuestionNode key={c.id} node={c} depth={depth + 1} ctx={ctx} />)}
        </div>
      )}
    </div>
  )
}

// A run of top-level contact fields shares one card.
function ContactCard ({ nodes, ctx }) {
  const anyMasked = nodes.some(n => (ctx.contactFields || []).some(f => f.key === n.contact_key && f.prefill_masked))
  return (
    <section aria-labelledby="qnr-contact-h" className="border border-gray-200 rounded-xl p-4 space-y-5 bg-white">
      <div>
        <h2 id="qnr-contact-h" className="text-lg font-bold text-gray-900">Your contact details</h2>
        {anyMasked && (
          <p className="text-sm text-gray-500 mt-0.5">
            For your privacy, details we already have are partly hidden.  Tap <strong>Yes</strong> if one is right, or <strong>Change</strong> to correct it.
          </p>
        )}
      </div>
      {nodes.map(n => <QuestionNode key={n.id} node={n} depth={1} ctx={ctx} />)}
    </section>
  )
}

/**
 * Props:
 *   questions      the snapshot tree
 *   answers        { [question_id]: value }  (the FULL map, hidden answers included)
 *   onAnswer(node, value)
 *   contactFields  /verify contact.fields (prefill_masked per key), or []
 *   contact        { [contact_key]: {confirmed:true} | {value} }
 *   onContact(key, value|null)
 *   errors         { [question_id]: { rule, rows?: { [i]: { [field]: rule } } } }
 *   disabled       read-only (submitting, link closed, answers unavailable)
 *   renderUpload(node)  the upload step for the upload question (else a placeholder)
 */
export default function QuestionnaireQuestionList ({
  questions, answers, onAnswer, contactFields = [], contact = {}, onContact, errors = {}, disabled = false,
  renderUpload = null, today = null,
}) {
  const index = indexTree(questions || [])
  const vis = makeVisibility(index, answers || {})
  const ctx = {
    answers: answers || {}, contact: contact || {}, contactFields, errors: errors || {}, disabled, vis,
    today: today || localToday(), renderUpload,
    onAnswer: (node, v) => onAnswer && onAnswer(node, v),
    onContact: (key, v) => onContact && onContact(key, v),
  }
  const top = (questions || []).filter(Boolean)
  const blocks = []
  for (const n of top) {
    const last = blocks[blocks.length - 1]
    if (n.type === 'contact_field') {
      if (last && last.contact) last.nodes.push(n)
      else blocks.push({ contact: true, nodes: [n] })
    } else blocks.push({ contact: false, nodes: [n] })
  }
  return (
    <div className="space-y-8">
      {blocks.map((b, i) => b.contact
        ? <ContactCard key={`c${i}`} nodes={b.nodes} ctx={ctx} />
        : <QuestionNode key={b.nodes[0].id} node={b.nodes[0]} depth={1} ctx={ctx} />)}
    </div>
  )
}

// For the caller's live-region announcement when an answer reveals or hides
// follow-ups: how many of `node`'s follow-ups are visible under `answers`.
export function visibleFollowUps (questions, node, answers) {
  const index = indexTree(questions || [])
  const vis = makeVisibility(index, answers || {})
  return (node.children || []).filter(c => vis(c))
}
